export const supportTicketStatuses = [
  "Open",
  "In Progress",
  "Waiting on Requester",
  "Resolved",
  "Closed",
] as const;

export const supportTicketPriorities = ["Low", "Normal", "High", "Urgent"] as const;

export const memberSupportTicketCategories = [
  "Account / Login",
  "Mobile App Issue",
  "Prayer / Iqamah",
  "Events / Announcements",
  "Ramadan / Iftar",
  "Profile",
  "Notification Issue",
  "General Question",
  "Other",
] as const;

export const shdeedSupportTicketCategories = [
  "Technical Issue",
  "User / Access Issue",
  "Mobile App",
  "Org Admin Portal",
  "Configuration",
  "Data / Migration",
  "Security / Privacy",
  "Feature Request",
  "Billing / Subscription",
  "Other",
] as const;

export const supportAttachmentMimeTypes = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
] as const;

export const supportAttachmentExtensions = ["jpg", "jpeg", "png", "webp", "heic", "heif"] as const;

export const maxSupportAttachmentsPerMessage = 5;
export const maxSupportAttachmentBytes = 10 * 1024 * 1024;
export const maxSupportSubjectLength = 140;
export const maxSupportDescriptionLength = 2_000;
export const minSupportSubjectLength = 3;
export const minSupportDescriptionLength = 10;

export type SupportTicketStatus = (typeof supportTicketStatuses)[number];
export type SupportTicketPriority = (typeof supportTicketPriorities)[number];
export type MemberSupportTicketCategory = (typeof memberSupportTicketCategories)[number];
export type ShdeedSupportTicketCategory = (typeof shdeedSupportTicketCategories)[number];
export type SupportAttachmentMimeType = (typeof supportAttachmentMimeTypes)[number];
export type SupportAttachmentExtension = (typeof supportAttachmentExtensions)[number];
export type SupportTicketKind = "member_to_organization" | "organization_to_shdeed";
export type SupportNotificationType =
  | "support.ticket.created"
  | "support.ticket.reply"
  | "support.ticket.status_changed"
  | "support.ticket.resolved";

export type SupportAttachmentReference = {
  fileName: string;
  mimeType: SupportAttachmentMimeType;
  sizeBytes: number;
  storagePath: string;
};

export type SupportTicketValidationDecision =
  | {
      accepted: true;
      value: string;
    }
  | {
      accepted: false;
      reason: "empty" | "too_short" | "too_long";
    };

export function isSupportTicketStatus(value: unknown): value is SupportTicketStatus {
  return typeof value === "string" && supportTicketStatuses.includes(value as SupportTicketStatus);
}

export function isSupportTicketPriority(value: unknown): value is SupportTicketPriority {
  return (
    typeof value === "string" && supportTicketPriorities.includes(value as SupportTicketPriority)
  );
}

export function isMemberSupportTicketCategory(
  value: unknown,
): value is MemberSupportTicketCategory {
  return (
    typeof value === "string" &&
    memberSupportTicketCategories.includes(value as MemberSupportTicketCategory)
  );
}

export function isShdeedSupportTicketCategory(
  value: unknown,
): value is ShdeedSupportTicketCategory {
  return (
    typeof value === "string" &&
    shdeedSupportTicketCategories.includes(value as ShdeedSupportTicketCategory)
  );
}

export function isSupportAttachmentMimeType(value: unknown): value is SupportAttachmentMimeType {
  return (
    typeof value === "string" &&
    supportAttachmentMimeTypes.includes(value as SupportAttachmentMimeType)
  );
}

export function isSupportAttachmentExtension(value: unknown): value is SupportAttachmentExtension {
  return (
    typeof value === "string" &&
    supportAttachmentExtensions.includes(value.toLowerCase() as SupportAttachmentExtension)
  );
}

export function normalizeSupportSubject(value: unknown): SupportTicketValidationDecision {
  return normalizeSupportText(value, {
    maxLength: maxSupportSubjectLength,
    minLength: minSupportSubjectLength,
  });
}

export function normalizeSupportDescription(value: unknown): SupportTicketValidationDecision {
  return normalizeSupportText(value, {
    maxLength: maxSupportDescriptionLength,
    minLength: minSupportDescriptionLength,
  });
}

export function isSafeSupportTicketId(value: unknown): value is string {
  return (
    typeof value === "string" &&
    /^[A-Za-z0-9][A-Za-z0-9_-]{1,119}$/u.test(value) &&
    !value.includes("/")
  );
}

export function isSafeSupportAttachmentFileName(value: unknown): value is string {
  if (typeof value !== "string") {
    return false;
  }

  const match = value.match(/^support-[0-9]{10,17}-[a-z0-9_-]{1,48}\.([A-Za-z0-9]+)$/u);
  return Boolean(match?.[1] && isSupportAttachmentExtension(match[1]));
}

export function buildMemberSupportTicketRoute(input: {
  organizationId: string;
  ticketId: string;
}): string {
  return `shdeed://support/member/${encodeURIComponent(input.organizationId)}/${encodeURIComponent(
    input.ticketId,
  )}`;
}

function normalizeSupportText(
  value: unknown,
  limits: {
    maxLength: number;
    minLength: number;
  },
): SupportTicketValidationDecision {
  if (typeof value !== "string") {
    return { accepted: false, reason: "empty" };
  }

  const normalized = value.trim().replace(/\s+/gu, " ");
  if (normalized.length === 0) {
    return { accepted: false, reason: "empty" };
  }
  if (normalized.length < limits.minLength) {
    return { accepted: false, reason: "too_short" };
  }
  if (normalized.length > limits.maxLength) {
    return { accepted: false, reason: "too_long" };
  }

  return {
    accepted: true,
    value: normalized,
  };
}
